import type { DefaultOptionType } from 'antd/lib/select';
import type { IntlShape } from 'react-intl';
import { getObisTranslation } from '@/utils/getObisTranslation';
import { MeterRegisters as messages } from '@/components/filter/messages';
import {
  CompressedAirRegister,
  CustomRegister,
  ElectricityRegister,
  GasRegister,
  MEDIUM_CONSUMPTION_REGISTERS,
  OilRegister,
  OtherRegister,
  REGISTER_MEDIUMS,
  RegisterMedium,
  ThermalRegister,
  WaterRegister,
} from '@enmon/commons';
import { createWildcardFullOBIS } from '@enmon/commons/lib/enmon';
import type { MeterRegister } from '@enmon/commons';

export type FullObisOption = DefaultOptionType & { value: string };

// Mirrors MEDIUM_CONSUMPTION_REGISTERS's medium groupings, but with each medium's full register
// enum instead of a curated consumption subset.
const MEDIUM_REGISTERS: Record<RegisterMedium, readonly string[]> = {
  [RegisterMedium.PressedAir]: Object.values(CompressedAirRegister),
  [RegisterMedium.Electricity]: Object.values(ElectricityRegister),
  [RegisterMedium.Gas]: Object.values(GasRegister),
  [RegisterMedium.Nitrogen]: Object.values(GasRegister),
  [RegisterMedium.Heating]: Object.values(ThermalRegister),
  [RegisterMedium.Cold]: Object.values(ThermalRegister),
  [RegisterMedium.Oil]: Object.values(OilRegister),
  [RegisterMedium.WarmWater]: Object.values(WaterRegister),
  [RegisterMedium.ColdWater]: Object.values(WaterRegister),
  [RegisterMedium.Custom]: Object.values(CustomRegister),
  [RegisterMedium.Other]: Object.values(OtherRegister),
};

/**
 * Static option list built from the full register catalog (all mediums × their OBIS codes) — no
 * meter/API query needed, since the catalog is a fixed, enumerable set. With `onlyConsumption`,
 * only consumption registers (MEDIUM_CONSUMPTION_REGISTERS) are offered.
 */
export const getFullObisOptions = (intl: IntlShape, onlyConsumption = false): FullObisOption[] =>
  REGISTER_MEDIUMS.flatMap(medium => {
    const obisCodes = onlyConsumption ? MEDIUM_CONSUMPTION_REGISTERS[medium] : MEDIUM_REGISTERS[medium];
    return obisCodes.map(obis => {
      const register = { medium, obis: obis as MeterRegister['obis'] };
      // NOTE: this catalog has no per-tariff entries (just medium × obis), so the value is always
      // wildcarded on tariff — the backend expands '*' to match a register regardless of tariff.
      const wildcardFullObis = createWildcardFullOBIS(register);
      return {
        value: wildcardFullObis,
        label: intl.formatMessage(messages.optionLabel, {
          name: getObisTranslation(intl, register),
          obis: wildcardFullObis,
        }),
      };
    });
  });
